import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Drawer,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import MailIcon from "../../assets/mail 1.png";
import { useApiRequest } from "../Login/Hooks/useApiRequest";
import { toast } from "react-toastify";

/**
 * Shared "claim a digital item" email + OTP bottom sheet.
 *
 * Owns only the email-collection/OTP-verification step (identical across all
 * call sites). What happens after a successful verify — calling the claim
 * API, showing a success dialog, navigating, bookkeeping — is caller-owned
 * and fired via `onVerified`, since that part genuinely differs per flow.
 */
export default function EmailVerifySheet({
  open,
  onClose,
  title,
  subtitle,
  imageUrl,
  onVerified,
  closeOnVerify = true,
}) {
  const token = localStorage.getItem("UserJWTAuth");
  const deviceId = localStorage.getItem("uuid");
  const { makeApiRequest } = useApiRequest();

  const [requestId, setRequestId] = useState(null);
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [timer, setTimer] = useState(30);
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mailIconBroken, setMailIconBroken] = useState(false);
  const [productImageBroken, setProductImageBroken] = useState(false);

  const emailRef = useRef(null);
  const otpRefs = useRef([]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const validateEmail = (value) => {
    const regex =
      /^(?!.*\.\.)(?!.*\.$)(?!\.)([a-zA-Z0-9]([a-zA-Z0-9._%+\-]*[a-zA-Z0-9])?)@([a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    return regex.test(value);
  };

  const isOtpComplete = otp.every((d) => d !== "") && otp.join("").length === 4;

  const getCommonHeaders = () => ({
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  });

  const resetState = () => {
    setStep(1);
    setEmail("");
    setOtp(["", "", "", ""]);
    setTimer(30);
    setEmailError("");
    setRequestId(null);
    setLoading(false);
  };

  // ── Side-effects ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) resetState();
  }, [open]);

  // A new item may have a working image even if the previous one didn't.
  useEffect(() => {
    setProductImageBroken(false);
  }, [imageUrl]);

  useEffect(() => {
    if (open) {
      const timeout = setTimeout(() => {
        if (step === 1) emailRef.current?.focus();
        else otpRefs.current[0]?.focus();
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [open, step]);

  useEffect(() => {
    if (step !== 2 || timer <= 0) return;
    const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [step, timer]);

  // ── API calls ──────────────────────────────────────────────────────────────

  const sendOtp = () => {
    if (!email) {
      setEmailError("Email is required");
      return;
    }
    if (!validateEmail(email)) {
      setEmailError("Enter a valid email address");
      return;
    }
    setEmailError("");
    setLoading(true);

    makeApiRequest({
      url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v1/digital/claim/email-verify`,
      method: "POST",
      headers: getCommonHeaders(),
      data: { email },
      handleSuccess: (res) => {
        setLoading(false);
        setRequestId(res?.data?.data?.requestId);
        setStep(2);
        setTimer(30);
      },
      handleError: (err) => {
        setLoading(false);
        const msg =
          err?.response?.data?.status?.message || "Failed to send OTP. Please try again.";
        setEmailError(msg);
      },
    });
  };

  const verifyOtp = () => {
    const code = otp.join("");
    setLoading(true);

    makeApiRequest({
      url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v1/digital/claim/email-verify/code`,
      method: "POST",
      headers: getCommonHeaders(),
      data: {
        requestId,
        otp: parseInt(code, 10),
      },
      handleSuccess: () => {
        setLoading(false);
        if (closeOnVerify) {
          resetState();
          onClose();
        }
        onVerified(email);
      },
      handleError: (err) => {
        setLoading(false);
        const msg =
          err?.response?.data?.status?.message || "Invalid OTP. Please try again.";
        toast.error(msg);
      },
    });
  };

  // ── OTP input handlers ─────────────────────────────────────────────────────

  const handleOtpChange = (value, idx) => {
    if (!/^[a-zA-Z0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[idx] = value.toUpperCase();
    setOtp(newOtp);

    if (value && idx < 3) {
      otpRefs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      if (otp[idx] === "" && idx > 0) {
        otpRefs.current[idx - 1]?.focus();
      } else {
        const newOtp = [...otp];
        newOtp[idx] = "";
        setOtp(newOtp);
      }
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").slice(0, 4);
    if (!/^[a-zA-Z0-9]+$/.test(paste)) return;

    const newOtp = paste.toUpperCase().split("").concat(["", "", "", ""]).slice(0, 4);
    setOtp(newOtp);
    otpRefs.current[Math.min(paste.length, 3)]?.focus();
  };

  const onChangeEmail = () => {
    setStep(1);
    setOtp(["", "", "", ""]);
    setEmail("");
    setEmailError("");
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={() => {
        resetState();
        onClose();
      }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          height: "70vh",
          overflow: "visible",
        },
      }}
    >
      {/* ── Floating mail icon ── */}
      <Box
        sx={{
          position: "absolute",
          top: -40,
          left: "50%",
          transform: "translateX(-50%)",
          width: 72,
          height: 72,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#fff",
          borderRadius: "50%",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
        }}
      >
        {mailIconBroken ? (
          <MailOutlineIcon sx={{ fontSize: 40, color: "#f97316" }} />
        ) : (
          <img
            src={MailIcon}
            alt="Mail"
            style={{ width: 72 }}
            onError={() => setMailIconBroken(true)}
          />
        )}
      </Box>

      <Box
        sx={{
          p: 3,
          pt: 6,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* ── Product card ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            borderRadius: 3,
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 50,
              height: 50,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
              flexShrink: 0,
              bgcolor: productImageBroken || !imageUrl ? "#f3f4f6" : "transparent",
              borderRadius: 1,
            }}
          >
            {productImageBroken || !imageUrl ? (
              <ImageNotSupportedOutlinedIcon sx={{ color: "#9ca3af", fontSize: 24 }} />
            ) : (
              <img
                src={imageUrl}
                alt={title}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={() => setProductImageBroken(true)}
              />
            )}
          </Box>

          <Box>
            <Typography fontWeight={600}>{title}</Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {/* ── Step 1 – Email input ── */}
        {step === 1 && (
          <>
            <Typography sx={{ fontSize: 14, mb: 1 }}>Enter Email</Typography>

            <TextField
              fullWidth
              placeholder="name@example.com"
              value={email}
              inputRef={emailRef}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && email && validateEmail(email)) sendOtp();
              }}
              error={!!emailError}
              helperText={emailError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineIcon sx={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  bgcolor: "#f9fafb",
                },
              }}
            />
          </>
        )}

        {/* ── Step 2 – OTP input ── */}
        {step === 2 && (
          <>
            <Box display="flex" justifyContent="center">
              <Typography variant="body2" mb={2} textAlign="center">
                Enter the 4-digit code sent to <strong>{email}</strong>.
              </Typography>
            </Box>

            <Box display="flex" justifyContent="center" mb={2}>
              <Box display="flex" gap={1.5}>
                {otp.map((digit, idx) => (
                  <TextField
                    key={idx}
                    value={digit}
                    inputRef={(el) => (otpRefs.current[idx] = el)}
                    onChange={(e) => handleOtpChange(e.target.value, idx)}
                    onKeyDown={(e) => handleKeyDown(e, idx)}
                    onPaste={handlePaste}
                    inputProps={{
                      maxLength: 1,
                      style: {
                        textAlign: "center",
                        fontSize: 20,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        padding: "14px 0",
                      },
                    }}
                    sx={{
                      width: 60,
                      "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Resend */}
            <Box display="flex" justifyContent="center">
              <Typography variant="body2" color="#6b7280">
                Didn't receive the code?{" "}
                {timer > 0 ? (
                  `Resend in ${timer}s`
                ) : (
                  <Box
                    component="span"
                    sx={{ color: "#00A86B", cursor: "pointer", fontWeight: 500 }}
                    onClick={sendOtp}
                  >
                    Resend
                  </Box>
                )}
              </Typography>
            </Box>

            {/* Change email */}
            <Box display="flex" justifyContent="center" mt={1}>
              <Typography
                variant="body2"
                onClick={onChangeEmail}
                sx={{
                  color: "#3B82F6",
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontWeight: 500,
                }}
              >
                Change email address
              </Typography>
            </Box>
          </>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/* ── Primary CTA ── */}
        <Button
          fullWidth
          variant="contained"
          disabled={
            loading || (step === 1 ? !email || !validateEmail(email) : !isOtpComplete)
          }
          onClick={step === 1 ? sendOtp : verifyOtp}
          sx={{
            borderRadius: 3,
            py: 1.5,
            bgcolor:
              (step === 1 ? email && validateEmail(email) : isOtpComplete) && !loading
                ? "#00A86B"
                : "#d1d5db",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 16,
            "&:hover": {
              bgcolor:
                (step === 1 ? email && validateEmail(email) : isOtpComplete) && !loading
                  ? "#009961"
                  : "#d1d5db",
            },
            "&.Mui-disabled": {
              bgcolor: "#d1d5db",
              color: "#fff",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={22} sx={{ color: "#fff" }} />
          ) : step === 1 ? (
            "Send OTP"
          ) : (
            "Verify"
          )}
        </Button>
      </Box>
    </Drawer>
  );
}
